/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useContext, useEffect } from 'react';
import { generateName } from './generateName';
import { AdvertContext } from './AdvertProviderDfp';
import { AdSlot, AdWrapper, Placeholder } from './Styles';
import {
    Size,
    Breakpoint,
    ZPG_DFP_NETWORK,
    DFP_AD_UNIT,
    CustomWindow,
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    AD_UNIT_PAGE
} from './types';
import { logInfo } from '@zoopla/logger';

declare const window: CustomWindow;

export type AdvertSlotDfpProps = {
    sizes: Size[];
    showOnly?: Breakpoint;
    position: number;
    adUnitCode?:
        | DFP_AD_UNIT
        | [AD_UNIT_ROOTS, AD_UNIT_SECTION]
        | [AD_UNIT_ROOTS, AD_UNIT_SECTION, AD_UNIT_PAGE]
        | [AD_UNIT_ROOTS, AD_UNIT_SECTION, string];
    dfpNetworkId?: ZPG_DFP_NETWORK.ID;
    collapseEmpty?: boolean;
    uuid?: string;
    adbridgClass?: string;
};

export const AdvertSlotDfp: FC<AdvertSlotDfpProps> = ({
    sizes,
    showOnly,
    position,
    adUnitCode: adUnitCodeFromProps,
    dfpNetworkId: dfpNetworkIdFromProps,
    collapseEmpty,
    uuid,
    adbridgClass,
    ...passThroughProps
}) => {
    const { state, mountSlot, unmountSlot } = useContext(AdvertContext);
    const {
        adUnitCode: adUnitCodeFromContext,
        dfpNetworkId: dfpNetworkIdFromContext,
        showPlaceHolderContent,
        debugMode,
        dfpInitialised,
        slots
    } = state;

    const isDev = process.env.NODE_ENV !== 'production';

    const size = sizes[0];

    const id = generateName({ size, position, uuid });
    const { isDefined: slotHasBeenDefined } = slots[id] || {};

    const height = typeof size !== 'string' ? size[1] : undefined;
    const [showOnlyDirection, showOnlyWidth] = showOnly || [];

    const adUnitCode = adUnitCodeFromProps || adUnitCodeFromContext;

    const adUnit = Array.isArray(adUnitCode)
        ? adUnitCode.length === 2
            ? `${adUnitCode[0]}${adUnitCode[1]}`
            : `${adUnitCode[0]}${adUnitCode[1]}${adUnitCode[2]}`
        : adUnitCode;

    const adUnitPath =
        (dfpNetworkIdFromProps || dfpNetworkIdFromContext) + adUnit;

    const hideFromSizeDown =
        showOnly && showOnlyDirection === '>' ? showOnlyWidth : undefined;
    const hideFromSizeUp =
        showOnly && showOnlyDirection === '<' ? showOnlyWidth : undefined;

    const log = (...args: unknown[]): void => {
        if (debugMode) {
            logInfo(`AdSlot(${id})`, ...args);
        }
    };

    // only display once slot has been defined in DfpManager via MOUNT_SLOT
    useEffect(() => {
        if (dfpInitialised && slotHasBeenDefined) {
            const windowRef = window;
            log('display()');
            windowRef.googletag = windowRef.googletag || {
                cmd: []
            };
            windowRef.googletag.cmd.push(function () {
                windowRef.googletag.display(id);
            });
        }
    }, [dfpInitialised, slotHasBeenDefined]);

    // only execute once on client side mount and re-mount if the id updates
    useEffect(() => {
        log('mountSlot');
        mountSlot({
            id,
            sizes,
            hideFromSizeDown,
            hideFromSizeUp,
            position,
            adUnitPath,
            collapseEmpty
        });
        return () => {
            log('unmountSlot');
            unmountSlot({ id });
        };
    }, [id]);

    return (
        <AdWrapper height={height} breakpoint={showOnly} {...passThroughProps}>
            <AdSlot
                itemScope={true}
                itemType="https://schema.org/WPAdBlock"
                id={id}
                height={height}
                data-testid={id}
                key={id}
                data-adbridg-ad-class={adbridgClass}
            >
                {isDev && showPlaceHolderContent && (
                    <Placeholder breakpoint={showOnly}>
                        {JSON.stringify(
                            {
                                id,
                                sizes,
                                position,
                                hideFromSizeDown,
                                hideFromSizeUp
                            },
                            null,
                            1
                        )}
                    </Placeholder>
                )}
            </AdSlot>
        </AdWrapper>
    );
};
